import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  documentService,
  templateService,
  DocumentGenerateRequest,
} from '../services/documentService';
import { Template } from '../models';

export const useDocumentViewModel = (consultationId?: number) => {
  const queryClient = useQueryClient();

  const {
    data: documents,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['documents', consultationId],
    queryFn: () => documentService.getList(consultationId),
    enabled: !!consultationId,
  });

  const generateMutation = useMutation({
    mutationFn: documentService.generate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents', consultationId] });
    },
  });

  const regenerateMutation = useMutation({
    mutationFn: documentService.regenerate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents', consultationId] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: documentService.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents', consultationId] });
    },
  });

  const downloadDocument = async (id: number, filename: string) => {
    const blob = await documentService.download(id);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  return {
    documents: documents ?? [],
    isLoading,
    error,
    refetch,
    generateDocument: (data: DocumentGenerateRequest) => generateMutation.mutateAsync(data),
    isGenerating: generateMutation.isPending,
    regenerateDocument: regenerateMutation.mutate,
    isRegenerating: regenerateMutation.isPending,
    deleteDocument: deleteMutation.mutate,
    isDeleting: deleteMutation.isPending,
    downloadDocument,
  };
};

export const useDocumentHistoryViewModel = (documentId: number) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['document-history', documentId],
    queryFn: () => documentService.getHistory(documentId),
    enabled: !!documentId,
  });

  return {
    history: data ?? [],
    isLoading,
    error,
  };
};

export const useTemplateViewModel = () => {
  const queryClient = useQueryClient();

  const {
    data: templates,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['templates'],
    queryFn: () => templateService.getList(),
  });

  const uploadMutation = useMutation({
    mutationFn: (options: {
      name: string;
      description: string;
      previewFile?: File;      // 배경용 HWPX
      generatorFile?: File;    // 생성용 HWPX
      file?: File;             // 레거시
      image?: File;
      fieldMapping?: Record<string, unknown>;
    }) => templateService.upload(options),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Template> }) =>
      templateService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: templateService.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
    },
  });

  return {
    templates: templates ?? [],
    isLoading,
    error,
    refetch,
    uploadTemplate: uploadMutation.mutateAsync,
    isUploading: uploadMutation.isPending,
    updateTemplate: updateMutation.mutate,
    isUpdating: updateMutation.isPending,
    deleteTemplate: deleteMutation.mutate,
    isDeleting: deleteMutation.isPending,
  };
};
